// Can WhatsApp deliver this office's "text" reminders? (WhatsApp-only offices, 2026-10-07)
//
// At an office with outbound SMS switched off (SMS_OUTBOUND_DISABLED), teamio-utility sends a text
// reminder ONLY as an approved WhatsApp template — utility.js "WhatsApp reminder branch": it needs
// WHATSAPP_ENABLED, a GENERAL_WHATSAPP_PHONE sender, and a WHATSAPP_TEMPLATES entry for the
// reminder's template key. Without one it falls back to SMS, the engine refuses, and the reminder
// silently never goes out — so the reminders page must refuse to save such a setting.
//
// Tests: cypress/node-tests/whatsapp-only-reminders.test.js (executes teamio-utility's real
// resolveWhatsAppTemplateSid and reads the keys from the exe + WhatsApp onboarding).

import { readSettingValue, hasSmsNumber, isSmsOutboundDisabled, isWhatsAppEnabled } from './siteCapability.js';

/** The WHATSAPP_TEMPLATES keys the exe tags reminder texts with (smsMsgAttributes.reminderType). */
export const WA_TEMPLATE_KEYS = Object.freeze({ appointment: 'apt-reminder', recall: 'recall-reminder' });

/**
 * Does WHATSAPP_TEMPLATES hold a usable template for `key`? MIRROR of teamio-utility
 * resolveWhatsAppTemplateSid(templates, key, anyLanguage) !== null — that resolver falls back to
 * 'en' then to the first non-empty language, so ANY non-empty entry counts.
 */
export function hasWhatsAppTemplate(templates, key) {
  let map = templates;
  if (typeof map === 'string') {
    try { map = JSON.parse(map); } catch (e) { return false; }
  }
  if (!map || typeof map !== 'object') return false;
  const entry = map[key];
  if (!entry) return false;
  if (typeof entry === 'string') return true;
  if (typeof entry === 'object') return Object.keys(entry).some((k) => !!entry[k]);
  return false;
}

/** Which reminder families WhatsApp can deliver for this site. */
export function whatsAppReminderCoverage(settings) {
  const sender = readSettingValue(settings, 'GENERAL_WHATSAPP_PHONE');
  // Stricter than utility (which only tests truthiness): a blank sender can never send.
  const ready = isWhatsAppEnabled(settings) && typeof sender === 'string' && sender.trim() !== '';
  const templates = readSettingValue(settings, 'WHATSAPP_TEMPLATES');
  return {
    appointment: ready && hasWhatsAppTemplate(templates, WA_TEMPLATE_KEYS.appointment),
    recall: ready && hasWhatsAppTemplate(templates, WA_TEMPLATE_KEYS.recall),
  };
}

const smsOutboundOff = (settings) => hasSmsNumber(settings) && isSmsOutboundDisabled(settings);
const on = (v) => String(v || '').trim().toUpperCase() === 'TRUE';

/**
 * Why the reminders page must NOT save, or null. Only at a WhatsApp-only office, only when a text
 * confirmation / reminder / same-day reminder is ON, and only when WhatsApp can't deliver it.
 *
 * @param {object|Array} settings  allSettings or the settings array
 * @param {{ textConfirmations?: string, textReminders?: string, textSameDayReminder?: string }} toggles
 * @returns {string|null}
 */
export function reminderTextSaveRefusal(settings, toggles = {}) {
  if (!smsOutboundOff(settings)) return null;
  const anyOn = on(toggles.textConfirmations) || on(toggles.textReminders) || on(toggles.textSameDayReminder);
  if (!anyOn) return null;
  if (whatsAppReminderCoverage(settings).appointment) return null;
  return 'Texting is turned off for this office, so text confirmations and reminders can only go out as an approved WhatsApp template — and this office has no WhatsApp template for appointment reminders yet. Turn the text confirmations and reminders off to save, or contact Teamio to add the WhatsApp template.';
}

/**
 * Recall can still EMAIL, so it is never refused — but its TEXTS won't go out at a WhatsApp-only
 * office without a WhatsApp recall template. Returns the warning, or null.
 */
export function recallTextWarning(settings) {
  if (!smsOutboundOff(settings)) return null;
  if (whatsAppReminderCoverage(settings).recall) return null;
  return 'Texting is turned off for this office and there is no WhatsApp template for recall reminders, so recall texts will not be sent. Recall emails still go out.';
}
